import {
	CurrencyEnum,
	useGenerateVatFromRegistrationLazyQuery,
	useGetAllLocalizationCountriesQuery,
	useGetAllOrganizationIndustriesQuery,
	useUpdateOrganizationMutation,
	type GetOrganizationQuery,
} from '@codezo/graphql';
import { AlertCircle, BadgeCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { SelectPopover } from '../../../components/ui/select-popover';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
	validateClientDetails,
	validateRegistrations,
	type RegistrationRule,
} from '../client-validation';

type Organization = NonNullable<GetOrganizationQuery['getOrganization']>;

const CURRENCY_OPTIONS = Object.values(CurrencyEnum).map((c) => ({ value: c, label: c }));

/** Registration numbers keyed by their type code (CVR, VAT, …). */
type Registrations = Record<string, string>;

function toRegistrationMap(regs: Organization['businessRegistrations']): Registrations {
	const map: Registrations = {};
	for (const r of regs ?? []) map[r.type] = r.number;
	return map;
}

/** Stable comparison for the dirty check — key order must not matter. */
function serializeRegistrations(regs: Registrations): string {
	return Object.entries(regs)
		.filter(([, v]) => v.trim())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([k, v]) => `${k}=${v.trim()}`)
		.join('|');
}

/**
 * The client's company record, as a dialog.
 *
 * ── Why this is not v1's `ClientInformationSection` ──
 *
 * That one is an inline card with its Cancel/Save in the card's own header — it
 * cannot be a dialog without being rewritten, and v1 is the live page. So the
 * rules live here instead, moved across unchanged: per-country registration
 * fields, VAT derived from a registration number, the warning about
 * registrations a country change will drop, and the touched/save-attempted
 * rule that stops a pre-filled form opening in red.
 *
 * That is a second copy of real business logic, and it should not stay one —
 * when v1 retires, this becomes the only copy; if v1 outlives this page, the
 * fields and the rules want lifting into one module both call. It is written to
 * be moved: nothing here knows it is in a dialog except the wrapper at the end.
 */
export function EditCompanyDetailsModal({
	open,
	onOpenChange,
	organization,
	onSaved,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	organization: Organization;
	onSaved: () => void;
}) {
	const org = organization;
	const savedCurrency = org.organizationSetting?.currency ?? null;

	/* ── Form state ── */
	const [name, setName] = useState(org.name);
	const [countryCode, setCountryCode] = useState(org.countryCode ?? '');
	const [websiteUrl, setWebsiteUrl] = useState(org.websiteUrl ?? '');
	const [industryId, setIndustryId] = useState(org.organizationIndustryId ?? '');
	const [currency, setCurrency] = useState<string>(savedCurrency ?? '');
	const [registrations, setRegistrations] = useState<Registrations>(() =>
		toRegistrationMap(org.businessRegistrations)
	);

	/* Which fields the user has actually been in. A pre-populated edit form must
	   not open covered in red for data it merely inherited — errors appear once
	   you touch a field, or all at once when you try to save. */
	const [touched, setTouched] = useState<Record<string, boolean>>({});
	const [saveAttempted, setSaveAttempted] = useState(false);
	const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));
	const showErrorFor = (field: string) => touched[field] || saveAttempted;

	/* Re-seed on the record changing AND on each open, so a cancelled edit does
	   not persist into the next one — the dialog unmounts nothing. */
	useEffect(() => {
		if (!open) return;
		setName(org.name);
		setCountryCode(org.countryCode ?? '');
		setWebsiteUrl(org.websiteUrl ?? '');
		setIndustryId(org.organizationIndustryId ?? '');
		setCurrency(org.organizationSetting?.currency ?? '');
		setRegistrations(toRegistrationMap(org.businessRegistrations));
		setTouched({});
		setSaveAttempted(false);
	}, [org, open]);

	/* ── Reference data ── */
	const { data: countriesData } = useGetAllLocalizationCountriesQuery({
		fetchPolicy: 'no-cache',
	});
	const { data: industryData } = useGetAllOrganizationIndustriesQuery({
		fetchPolicy: 'no-cache',
	});

	const selectedCountry = useMemo(
		() =>
			countriesData?.getAllLocalizationCountries?.find((c) => c.code === countryCode) ?? null,
		[countriesData, countryCode]
	);

	const countryOptions = useMemo(
		() =>
			countriesData?.getAllLocalizationCountries?.map((c) => ({
				value: c.code,
				label: c.name,
			})) ?? [],
		[countriesData]
	);

	const industryOptions = useMemo(
		() =>
			industryData?.getAllOrganizationIndustries?.map((i) => ({
				value: i.id,
				label: i.name,
			})) ?? [],
		[industryData]
	);

	/* The registration fields the *selected* country asks for. */
	const countryRegistrations = useMemo(
		() => selectedCountry?.registrations ?? [],
		[selectedCountry]
	);

	/**
	 * Saved registrations the newly-selected country has no field for. They are
	 * dropped on save, so say so rather than losing them silently.
	 */
	const droppedRegistrations = useMemo(() => {
		if (!selectedCountry) return [];
		const applicable = new Set(countryRegistrations.map((r) => r.code));
		return (org.businessRegistrations ?? []).filter((r) => !applicable.has(r.type));
	}, [selectedCountry, countryRegistrations, org.businessRegistrations]);

	/* ── VAT derivation ── */
	const [generateVat] = useGenerateVatFromRegistrationLazyQuery();

	const handleRegistrationChange = useCallback(
		async (code: string, value: string) => {
			setRegistrations((prev) => ({ ...prev, [code]: value }));

			const reg = countryRegistrations.find((r) => r.code === code);
			if (!reg?.vatDerivationPattern || !value || !countryCode) return;

			try {
				const { data } = await generateVat({
					variables: {
						countryCode,
						registrationNumber: value,
						registrationType: code,
					},
				});
				const derived = data?.generateVATFromRegistration;
				/* Only fill an empty VAT — never overwrite one an admin corrected by hand. */
				if (derived)
					setRegistrations((prev) => (prev.VAT ? prev : { ...prev, VAT: derived }));
			} catch {
				/* Derivation is a convenience; the field stays editable either way. */
			}
		},
		[countryRegistrations, countryCode, generateVat]
	);

	/* ── Dirty check ── */
	const savedRegistrationKey = useMemo(
		() => serializeRegistrations(toRegistrationMap(org.businessRegistrations)),
		[org.businessRegistrations]
	);
	const currentRegistrationKey = serializeRegistrations(
		/* Values for fields the selected country doesn't show are not part of the form. */
		Object.fromEntries(
			countryRegistrations
				.map((r) => [r.code, registrations[r.code] ?? ''])
				.filter(([, v]) => v)
		)
	);

	const registrationsDirty = currentRegistrationKey !== savedRegistrationKey;
	const dirty =
		name !== org.name ||
		countryCode !== (org.countryCode ?? '') ||
		websiteUrl !== (org.websiteUrl ?? '') ||
		industryId !== (org.organizationIndustryId ?? '') ||
		currency !== (savedCurrency ?? '') ||
		registrationsDirty;

	/* ── Validation ──
	   Errors are computed from the live values so Save is gated correctly from
	   the first keystroke, but *shown* from the debounced values and only for
	   fields you have been in — so a half-typed CVR is not flagged on its third
	   character, and an untouched form never opens in red. ── */
	const detailErrors = validateClientDetails({ name, countryCode });

	const registrationErrors = useMemo(
		() => validateRegistrations(registrations, countryRegistrations as RegistrationRule[]),
		[registrations, countryRegistrations]
	);

	const debouncedRegistrations = useDebouncedValue(registrations, 500);
	const debouncedRegistrationErrors = useMemo(
		() =>
			validateRegistrations(
				debouncedRegistrations,
				countryRegistrations as RegistrationRule[]
			),
		[debouncedRegistrations, countryRegistrations]
	);

	const shownDetailError = (field: 'name' | 'countryCode') =>
		showErrorFor(field) ? detailErrors[field] : undefined;
	const shownRegistrationError = (code: string) =>
		showErrorFor(code) ? debouncedRegistrationErrors[code] : undefined;

	const errorCount = Object.keys(detailErrors).length + Object.keys(registrationErrors).length;
	const valid = errorCount === 0;

	/* ── Save ── */
	const [updateOrganization, { loading: saving }] = useUpdateOrganizationMutation();

	const handleSubmit = async () => {
		setSaveAttempted(true);
		if (!valid) {
			toast.error(Object.values({ ...detailErrors, ...registrationErrors })[0]);
			return;
		}

		const regs = countryRegistrations
			.map((r) => ({ type: r.code, number: (registrations[r.code] ?? '').trim() }))
			.filter((r) => r.number);

		try {
			const result = await updateOrganization({
				variables: {
					input: {
						id: org.id,
						name: name.trim(),
						countryCode: countryCode || undefined,
						websiteUrl: websiteUrl.trim(),
						/* null clears the industry; the API leaves it alone only on omit. */
						organizationIndustryId: industryId || null,
						...(registrationsDirty ? { businessRegistrations: regs } : {}),
						...(currency ? { settings: { currency: currency as CurrencyEnum } } : {}),
					},
				},
			});

			/* MessageResponse.success is the real verdict — the mutation resolves
			   without throwing on a rejected update, so trusting the absence of an
			   exception would report a save that never happened. */
			const response = result.data?.updateOrganization;
			if (!response?.success) {
				toast.error(response?.message || 'Failed to update client');
				return;
			}

			toast.success('Client information updated');
			onSaved();
			onOpenChange(false);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update client');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Edit company details</DialogTitle>
					<DialogDescription>
						The client&rsquo;s own record — what appears on quotes and invoices.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-5">
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<Field
									label="Client name"
									required
									error={shownDetailError('name')}
								>
									<Input
										value={name}
										onChange={(e) => setName(e.target.value)}
										onBlur={() => markTouched('name')}
										placeholder="e.g. Acme Corporation"
										aria-invalid={!!shownDetailError('name')}
									/>
								</Field>
								<Field label="Website">
									<Input
										value={websiteUrl}
										onChange={(e) => setWebsiteUrl(e.target.value)}
										placeholder="https://example.com"
									/>
								</Field>
								<Field
									label="Country"
									required
									error={shownDetailError('countryCode')}
								>
									<SelectPopover
										value={countryCode}
										onChange={(v) => {
											markTouched('countryCode');
											setCountryCode(v);
										}}
										options={countryOptions}
										placeholder="Select country"
									/>
								</Field>
								<Field label="Industry">
									<SelectPopover
										value={industryId}
										onChange={setIndustryId}
										options={industryOptions}
										placeholder="Select industry"
									/>
								</Field>
							</div>

							{/* ── Business registrations ── */}
							{countryRegistrations.length > 0 && (
								<>
									<div className="border-t border-line-1" />
									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										{countryRegistrations.map((reg) => {
											const saved = (org.businessRegistrations ?? []).find(
												(r) => r.type === reg.code
											);
											const unchanged =
												(registrations[reg.code] ?? '') ===
												(saved?.number ?? '');
											const error = shownRegistrationError(reg.code);
											return (
												<Field
													key={reg.code}
													label={reg.label}
													required={reg.isRequired}
													error={error}
												>
													<div className="relative">
														<Input
															value={registrations[reg.code] ?? ''}
															onChange={(e) =>
																handleRegistrationChange(
																	reg.code,
																	e.target.value
																)
															}
															onBlur={() => markTouched(reg.code)}
															placeholder={
																reg.placeholder || undefined
															}
															aria-invalid={!!error}
														/>
														{error ? (
															<div className="absolute right-2.5 top-1/2 -translate-y-1/2">
																<AlertCircle
																	size={11}
																	className="text-destructive"
																/>
															</div>
														) : (
															saved?.verified &&
															unchanged && (
																<div
																	className="absolute right-2.5 top-1/2 -translate-y-1/2"
																	title="Verified against the business registry"
																>
																	<BadgeCheck
																		size={13}
																		className="text-status-success"
																	/>
																</div>
															)
														)}
													</div>
												</Field>
											);
										})}
									</div>
									{droppedRegistrations.length > 0 && (
										<p className="text-xs text-status-warn">
											{droppedRegistrations
												.map((r) => `${r.type} ${r.number}`)
												.join(', ')}{' '}
											{droppedRegistrations.length === 1 ? 'does' : 'do'} not
											apply in {selectedCountry?.name} and will be removed
											when you save.
										</p>
									)}
								</>
							)}

							{/* ── Billing currency ── */}
							<div className="border-t border-line-1" />
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<Field label="Currency">
									<SelectPopover
										value={currency}
										onChange={setCurrency}
										options={CURRENCY_OPTIONS}
										placeholder="Select currency"
									/>
								</Field>
							</div>
							<p className="text-xs text-text-4">
								The currency this client is quoted and invoiced in. Payment terms
								and other billing rules live under Settings.
							</p>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={saving}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!dirty || saving}
							title={
								valid
									? undefined
									: `Fix ${errorCount} ${errorCount === 1 ? 'field' : 'fields'} before saving`
							}
						>
							{saving ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
